import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";

function QuizPage() {
    const { id } = useParams();

    const [quiz, setQuiz] = useState(null);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [selectedOption, setSelectedOption] = useState(null);
    const [feedback, setFeedback] = useState(null);
    const [score, setScore] = useState(0);
    const [showNext, setShowNext] = useState(false);
    const [isFinished, setIsFinished] = useState(false);

    // 🔊 Sound effects
    const correctSound = useRef(
        new Audio("/sounds/correct.wav")
    );

    const wrongSound = useRef(
        new Audio("/sounds/wrong.wav")
    );

    const clickSound = useRef(
        new Audio("/sounds/click.wav")
    );

    // 🔊 Finish sounds
    const finishSuccessSound = useRef(
        new Audio("/sounds/finish_success.wav")
    );

    const finishFailSound = useRef(
        new Audio("/sounds/finish_fail.wav")
    );

    // Prevent finish sound from playing more than once
    const finishPlayed = useRef(false);

    // ✅ Load quiz
    useEffect(() => {
        const API_BASE = (
            process.env.REACT_APP_API_URL ||
            process.env.REACT_APP_API_BASE_URL ||
            "http://localhost:8000/api"
        ).replace(/\/+$/, "");

        const QUIZ_URL = `${ API_BASE }/quizzes/${ id }/`;

        fetch(QUIZ_URL)
            .then((res) => {
                if (!res.ok) {
                    throw new Error(
                        `HTTP error! status: ${ res.status }`
                    );
                }

                return res.json();
            })
            .then((data) => setQuiz(data))
            .catch((err) =>
                console.error("❌ Failed to fetch quiz:", err)
            );
    }, [id]);

    if (!quiz) {
        return <p>Loading quiz...</p>;
    }

    const questions = quiz.questions || [];
    const question = questions[currentQuestionIndex];

    // 🔊 Select answer
    const handleOptionSelect = (option) => {
        if (showNext) return;

        clickSound.current.currentTime = 0;

        clickSound.current.play().catch((err) => {
            console.warn(
                "Click sound could not play:",
                err
            );
        });

        setSelectedOption(option);
    };

    // 🔊 Submit answer
    const handleSubmit = () => {
        if (!selectedOption) {
            setFeedback(
                "❗ Please select an answer before submitting!"
            );
            return;
        }

        const correctOption = question.options.find(
            (opt) => opt.is_correct
        );

        if (selectedOption.id === correctOption.id) {
            setFeedback("✅ Correct!");

            // Add one point
            setScore((prev) => prev + 1);

            // 🔊 Correct sound
            correctSound.current.currentTime = 0;

            correctSound.current.play().catch((err) => {
                console.warn(
                    "Correct sound could not play:",
                    err
                );
            });
        } else {
            setFeedback(
                `❌ Wrong! Correct answer: ${ correctOption.text }`
            );

            // 🔊 Wrong sound
            wrongSound.current.currentTime = 0;

            wrongSound.current.play().catch((err) => {
                console.warn(
                    "Wrong sound could not play:",
                    err
                );
            });
        }

        setShowNext(true);
    };

    // ➡️ Next question / Finish quiz
    const handleNext = () => {
        if (currentQuestionIndex < questions.length - 1) {
            // Move to next question
            setCurrentQuestionIndex((prev) => prev + 1);
            setSelectedOption(null);
            setFeedback(null);
            setShowNext(false);
        } else {
            // 🎉 Last question has been completed

            // IMPORTANT:
            // score already includes the last answer because
            // handleSubmit() updated it before the user clicked Next.
            const finalScore = score;

            // Calculate percentage
            const percentage =
                questions.length > 0
                    ? (finalScore / questions.length) * 100
                    : 0;

            // 🔊 Play finish sound only once
            if (!finishPlayed.current) {
                if (percentage >= 50) {
                    finishSuccessSound.current.currentTime = 0;

                    finishSuccessSound.current
                        .play()
                        .catch((err) => {
                            console.warn(
                                "Finish success sound could not play:",
                                err
                            );
                        });
                } else {
                    finishFailSound.current.currentTime = 0;

                    finishFailSound.current
                        .play()
                        .catch((err) => {
                            console.warn(
                                "Finish fail sound could not play:",
                                err
                            );
                        });
                }

                finishPlayed.current = true;
            }

            setIsFinished(true);
        }
    };

    // 🎉 Finished screen
    if (isFinished) {
        return (
            <div
                style={{
                    textAlign: "center",
                    padding: "12rem 2rem",
                }}
            >
                <h2>🎉 Quiz Completed!</h2>

                <p>
                    Your score: {score} / {questions.length}
                </p>

                <div style={{ marginTop: "1.5rem" }}>
                    {/* ✅ Back to the main FunlishLand homepage */}
                    <a
                        href="https://www.funlishland.com"
                        style={{
                            marginRight: "10px",
                            backgroundColor: "#ccc",
                            color: "#000",
                            padding: "6px 14px",
                            borderRadius: "6px",
                            textDecoration: "none",
                            fontWeight: "bold",
                        }}
                    >
                        ← Back to Main Homepage
                    </a>

                    {/* ✅ Back to the React quizzes list page */}
                    <a
                        href="/"
                        style={{
                            backgroundColor: "#ccc",
                            color: "#000",
                            padding: "6px 14px",
                            borderRadius: "6px",
                            textDecoration: "none",
                            fontWeight: "bold",
                        }}
                    >
                        ← Back to Quizzes
                    </a>
                </div>
            </div>
        );
    }

    return (
        <div
            style={{
                textAlign: "center",
                padding: "10rem",
            }}
        >
            <h2>{quiz.title}</h2>

            <p style={{ color: "#555" }}>
                Question {currentQuestionIndex + 1} of{" "}
                {questions.length}
            </p>

            {question && (
                <div
                    key={question.id}
                    style={{
                        margin: "1rem auto",
                        maxWidth: "500px",
                    }}
                >
                    <h3
                        style={{
                            marginBottom: "1rem",
                        }}
                    >
                        {question.text}
                    </h3>

                    {question.options.map((option) => {
                        let style = {
                            display: "block",
                            padding: "10px 14px",
                            margin: "0.5rem auto",
                            borderRadius: "6px",
                            cursor: "pointer",
                            border: "1px solid #ccc",
                            backgroundColor: "#fff",
                            width: "80%",
                            textAlign: "left",
                            transition: "0.2s",
                        };

                        // Selected answer
                        if (
                            selectedOption?.id === option.id
                        ) {
                            style.border =
                                "2px solid #007bff";

                            style.backgroundColor =
                                "#eaf3ff";
                        }

                        // After submission
                        if (showNext) {
                            const correctOption =
                                question.options.find(
                                    (opt) => opt.is_correct
                                );

                            if (
                                option.id ===
                                correctOption.id
                            ) {
                                style.backgroundColor =
                                    "#4caf50";

                                style.color = "white";
                            } else if (
                                selectedOption?.id ===
                                option.id
                            ) {
                                style.backgroundColor =
                                    "#f44336";

                                style.color = "white";
                            }
                        }

                        return (
                            <button
                                key={option.id}
                                onClick={() =>
                                    handleOptionSelect(option)
                                }
                                disabled={showNext}
                                style={style}
                            >
                                {option.text}
                            </button>
                        );
                    })}

                    {/* Submit button */}
                    {!showNext && (
                        <button
                            onClick={handleSubmit}
                            style={{
                                marginTop: "1rem",
                                backgroundColor: "#28a745",
                                color: "white",
                                padding: "10px 20px",
                                borderRadius: "6px",
                                border: "none",
                                cursor: "pointer",
                                fontWeight: "bold",
                            }}
                        >
                            Submit
                        </button>
                    )}

                    {/* Next button */}
                    {showNext && (
                        <button
                            onClick={handleNext}
                            style={{
                                marginTop: "1rem",
                                backgroundColor: "#007bff",
                                color: "white",
                                padding: "10px 20px",
                                borderRadius: "6px",
                                border: "none",
                                cursor: "pointer",
                                fontWeight: "bold",
                            }}
                        >
                            Next ➜
                        </button>
                    )}

                    {/* Feedback */}
                    {feedback && (
                        <p
                            style={{
                                marginTop: "1rem",
                                fontWeight: "bold",
                                color: feedback.includes(
                                    "Correct"
                                )
                                    ? "green"
                                    : "red",
                            }}
                        >
                            {feedback}
                        </p>
                    )}
                </div>
            )}
        </div>
    );
}

export default QuizPage;